import { useEffect, useRef } from 'react';
import { Mail, ArrowRight } from 'lucide-react';
import { resetCookieConsent } from '../lib/tracking';

// Linki do stron lokalnych (/lp/*). Bez nich byly sierotami tylko z sitemap,
// a Google zostawil je jako "wykryta, obecnie niezindeksowana".
const CITY_GROUPS = [
  {
    label: 'Trójmiasto',
    links: [
      { name: 'Gdynia', href: '/lp/automatyzacja-ai-gdynia' },
      { name: 'Gdańsk', href: '/lp/agencja-ai-gdansk' },
      { name: 'Sopot', href: '/lp/automatyzacja-ai-sopot' },
      { name: 'Całe Trójmiasto', href: '/lp/automatyzacja-ai-trojmiasto' },
    ],
  },
  {
    label: 'Zdalnie',
    links: [
      { name: 'Warszawa', href: '/lp/systemy-ai-dla-firm-warszawa' },
      { name: 'Kraków', href: '/lp/automatyzacja-ai-dla-firm-krakow' },
      { name: 'Wrocław', href: '/lp/custom-software-i-ai-wroclaw' },
      { name: 'Poznań', href: '/lp/automatyzacja-ai-ecommerce-poznan' },
      { name: 'Śląsk', href: '/lp/systemy-ai-dla-firm-slask' },
    ],
  },
];

function handleCookieReset() {
  resetCookieConsent();
  window.dispatchEvent(new Event('daien-cookie-reset'));
}

export default function Footer() {
  const marqueeRef = useRef(null);

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    let cancelled = false;

    // Dynamic GSAP - marquee animation laduje sie tylko gdy footer w viewport.
    // Footer to global komponent, ale animacja nieblokujaca - lazy load OK.
    import('gsap').then(({ gsap }) => {
      if (cancelled) return;
      const ctx = gsap.context(() => {
        gsap.to(".marquee-inner", {
          xPercent: -50,
          duration: 40,
          ease: "none",
          repeat: -1,
        });
      }, marqueeRef);
      cleanup = () => ctx.revert();
    });

    return () => {
      cancelled = true;
      if (cleanup) cleanup();
    };
  }, []);

  return (
    <footer className="relative bg-bg pt-16 md:pt-20 pb-8 md:pb-12 overflow-hidden mt-20 rounded-t-[3rem] md:rounded-t-[5rem] border-t border-stroke">

      {/* Background Video Flipped Vertically */}
      <div className="absolute inset-0 z-0 opacity-40">
        <video
          src="https://stream.mux.com/Aa02T7oM1wH5Mk5EEVDYhbZ1ChcdhRsS2m1NYyx4Ua1g.m3u8"
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover scale-y-[-1]"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-bg/60 backdrop-blur-sm"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/80 to-transparent"></div>
      </div>

      <div className="relative z-10">
        {/* GSAP Marquee - single track, duplicated for seamless loop */}
        <div className="overflow-hidden border-y border-stroke/50 py-4 mb-16 bg-bg/40 backdrop-blur-md" ref={marqueeRef}>
          <div className="marquee-inner flex whitespace-nowrap">
            {[...Array(2)].map((_, dupIdx) => (
              <div key={dupIdx} className="flex shrink-0">
                {[...Array(5)].map((_, i) => (
                  <span key={`${dupIdx}-${i}`} className="text-4xl md:text-6xl lg:text-8xl font-display italic text-text-primary px-8">
                    BUDUJĘ · TESTUJĘ · SKALUJĘ <span className="text-stroke">•</span>
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16 text-center flex flex-col items-center">

          <h2 className="text-3xl md:text-5xl font-light text-text-primary mb-8 max-w-2xl leading-tight">
            Gotowy żeby skalować <span className="font-display italic">bez zatrudniania?</span>
          </h2>

          {/* Email CTA */}
          <a href="mailto:dawid@daien.pl" className="group relative inline-flex items-center justify-center mb-24">
            <div className="absolute inset-[-4px] accent-gradient rounded-full blur opacity-20 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
            <div className="relative border-gradient bg-surface px-8 py-5 rounded-full flex items-center gap-4 hover:bg-bg transition-colors duration-300">
              <Mail className="w-5 h-5 text-text-primary" />
              <span className="text-lg font-medium text-text-primary">dawid@daien.pl</span>
              <ArrowRight className="w-5 h-5 text-text-primary group-hover:translate-x-2 transition-transform" />
            </div>
          </a>

          {/* Footer Bar */}
          <div className="w-full pt-8 border-t border-stroke/50 space-y-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-6 text-sm text-muted">
                <a href="https://www.linkedin.com/in/dawid-st" target="_blank" rel="noopener noreferrer" className="hover:text-text-primary transition-colors">LinkedIn</a>
                <a href="https://www.instagram.com/_stepiendawid/" target="_blank" rel="noopener noreferrer" className="hover:text-text-primary transition-colors">Instagram</a>
                <a href="https://www.youtube.com/@DAIEN-d" target="_blank" rel="noopener noreferrer" className="hover:text-text-primary transition-colors">YouTube</a>
                <a href="https://github.com/WujsST" target="_blank" rel="noopener noreferrer" className="hover:text-text-primary transition-colors">GitHub</a>
              </div>

              <div className="flex items-center gap-3 bg-surface/50 border border-stroke/50 px-4 py-2 rounded-full">
                <div className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                </div>
                <span className="text-xs font-medium text-text-primary uppercase tracking-wider">Dostępny dla nowych projektów</span>
              </div>

              <div className="text-sm text-muted">
                © {new Date().getFullYear()} DAIEN · Systemy AI
              </div>
            </div>

            <p className="text-sm text-muted text-center md:text-left max-w-3xl">
              DAIEN to firma z Gdyni, która wdraża automatyzacje AI i aplikacje dla firm z Gdańska, Gdyni, Sopotu i całego Trójmiasta. Pracujemy też zdalnie z firmami w całej Polsce.
            </p>

            <nav aria-label="Obsługiwane miasta" className="flex flex-col gap-2 text-sm text-muted text-center md:text-left">
              {CITY_GROUPS.map((group) => (
                <div key={group.label} className="flex flex-wrap items-center justify-center md:justify-start gap-x-4 gap-y-2">
                  <span className="text-text-primary">{group.label}:</span>
                  {group.links.map((city) => (
                    <a key={city.href} href={city.href} className="hover:text-text-primary transition-colors">{city.name}</a>
                  ))}
                </div>
              ))}
            </nav>

            <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-muted">
              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2">
                <a href="/polityka-prywatnosci" className="hover:text-text-primary transition-colors">Polityka prywatności</a>
                <a href="/rodo" className="hover:text-text-primary transition-colors">RODO</a>
                <a href="/polityka-cookies" className="hover:text-text-primary transition-colors">Polityka cookies</a>
                <a href="/regulamin" className="hover:text-text-primary transition-colors">Regulamin</a>
                <button type="button" onClick={handleCookieReset} className="hover:text-text-primary transition-colors underline-offset-2 hover:underline">
                  Zmień ustawienia cookies
                </button>
              </div>
              <div className="text-center md:text-right">
                Dawid Stępień · ul. Żelazna 14 lok. 37, 81-150 Gdynia · NIP 9581764814
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
